import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { PageHeader } from '../components/PageHeader';
import { Plus, Edit2, Trash2, Wrench, AlertTriangle, CalendarClock, ShieldCheck, ChevronDown, CheckCircle2, Activity } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { formatDate, formatCurrency } from '../lib/format';
import { useToast } from '../hooks/use-toast';
import { EquipmentCell, EquipmentOptionLabel } from '../components/EquipmentCell';

const initialForm = { vehicle_id: '', maintenance_type: 'preventive', description: '', cost: 0, performed_by: '', date_performed: '', next_due: '', status: 'scheduled', remarks: '' };

const Maintenance = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);

  const canEdit = hasRole(user, 'super_admin','admin','supervisor');
  const canDelete = hasRole(user, 'super_admin','admin');

  const load = useCallback(async () => {
    const r = await api.get('/maintenance'); setItems(r.data);
    const v = await api.get('/vehicles'); setVehicles(v.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const vehById = (id) => vehicles.find((v) => v.id === id) || {};

  const openNew = () => { setEditing(null); setForm(initialForm); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...initialForm, ...m, cost: m.cost || 0 }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, cost: Number(form.cost) || 0 };
      if (editing) await api.put(`/maintenance/${editing.id}`, payload);
      else await api.post('/maintenance', payload);
      toast({ title: editing ? 'Updated' : 'Created' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  const remove = async (m) => { if (!window.confirm('Delete?')) return; await api.delete(`/maintenance/${m.id}`); toast({ title: 'Deleted' }); load(); };

  // Maintenance health derived from current data
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const in30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const isOverdue = (m) => m.next_due && m.status !== 'completed' && new Date(m.next_due) < today;
  const isDueSoon = (m) => m.next_due && m.status !== 'completed' && new Date(m.next_due) >= today && new Date(m.next_due) <= in30Days;
  const overdueRecords = items.filter(isOverdue);
  const dueSoonRecords = items.filter(isDueSoon);
  const scheduledCount = items.filter((m) => m.status === 'scheduled' || m.status === 'in_progress').length;
  const totalCount = items.length;
  const totalCost = items.filter((m) => m.status === 'completed').reduce((s, m) => s + (Number(m.cost) || 0), 0);
  const fleetSize = vehicles.length;
  const [howOpen, setHowOpen] = useState(false);
  const [filterMode, setFilterMode] = useState('all'); // all | overdue | due_soon | scheduled | completed
  const filtered = items.filter((m) => {
    if (filterMode === 'overdue') return isOverdue(m);
    if (filterMode === 'due_soon') return isDueSoon(m);
    if (filterMode === 'scheduled') return m.status === 'scheduled' || m.status === 'in_progress';
    if (filterMode === 'completed') return m.status === 'completed';
    return true;
  });

  return (
    <div className="space-y-5">
      {/* ===== SECTION 1: Hero / Value Proposition ===== */}
      <Card className="overflow-hidden border-slate-200">
        <CardContent className="p-0">
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr]">
            <div className="p-6 lg:p-8 bg-gradient-to-br from-[hsl(217,91%,24%)] to-[hsl(217,91%,18%)] text-white">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-300 mb-3">
                <ShieldCheck className="h-3.5 w-3.5" /> Proactive fleet maintenance
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold leading-tight">Stop chasing paperwork. Start preventing breakdowns.</h2>
              <p className="mt-3 text-blue-100/90 max-w-xl text-sm leading-relaxed">
                Every service event, meter reading, and operator inspection becomes a single source of truth — so your motor pool knows what needs attention <span className="text-amber-300 font-semibold">before</span> it fails. Move from reactive repairs to proactive fleet care across <span className="font-semibold text-white">{fleetSize}</span> equipment units.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {canEdit && (
                  <Button onClick={openNew} className="bg-amber-400 text-[hsl(217,91%,18%)] hover:bg-amber-300 font-semibold">
                    <Plus className="h-4 w-4 mr-1.5" /> Schedule Preventive Maintenance
                  </Button>
                )}
                <Button variant="outline" onClick={() => setHowOpen((v) => !v)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white">
                  How it works <ChevronDown className={`h-4 w-4 ml-1.5 transition-transform ${howOpen ? 'rotate-180' : ''}`} />
                </Button>
              </div>
            </div>
            <div className="p-6 lg:p-8 bg-slate-50 border-l border-slate-200">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Key benefits</div>
              <ul className="space-y-2.5 text-sm text-slate-700">
                {[
                  'Eliminate manual logbooks and lost service histories',
                  'Auto-flag equipment due for preventive service',
                  'Track every peso against the right asset and contract',
                  'Generate audit-ready maintenance trails in seconds',
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> {b}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Collapsible "How It Works" */}
          {howOpen && (
            <div className="border-t border-slate-200 p-6 lg:p-8 bg-white space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(217,91%,24%)] mb-2 flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> Creating Records</div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Every maintenance event — preventive or corrective — is captured with type, description, technician, cost, date performed, and the next-due milestone. Records are bound to a specific <span className="font-semibold">Equipment Code</span>, so the full service history is one click away.
                  </p>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(217,91%,24%)] mb-2 flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> Identifying Needs</div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Three signals surface what to service next: (1) preventive <span className="font-semibold">calendar/meter schedules</span>, (2) <span className="font-semibold">utilization hours</span> &amp; odometer readings, and (3) <span className="font-semibold">operator inspection</span> findings. When a threshold is crossed, the equipment appears in <span className="font-semibold">Due Soon</span> / <span className="font-semibold">Overdue</span> and notifications are dispatched.
                  </p>
                </div>
              </div>

              {/* Mini comparison */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Paper / spreadsheet vs. ESD</div>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-700">
                      <tr><th className="p-2.5 text-left">Aspect</th><th className="p-2.5 text-left">Paper / Spreadsheet</th><th className="p-2.5 text-left">ESD Maintenance (FMIS)</th></tr>
                    </thead>
                    <tbody className="divide-y">
                      {[
                        ['Record creation', 'Handwritten / re-typed', 'Structured form, validated'],
                        ['Data integrity', 'Loss-prone, illegible', 'Audit-logged, immutable'],
                        ['Asset linkage', 'Cross-referenced by hand', 'Auto-linked to Equipment Code'],
                        ['Searchability', 'Filing-cabinet lookups', 'Instant filter by status, period, type'],
                        ['Cost reporting', 'Re-keyed into spreadsheets', 'Aggregated automatically'],
                        ['Approval trail', 'Email threads / signatures', 'Built-in approval & audit log'],
                      ].map((row) => (
                        <tr key={row[0]}>
                          <td className="p-2.5 font-medium">{row[0]}</td>
                          <td className="p-2.5 text-slate-600">{row[1]}</td>
                          <td className="p-2.5 text-emerald-700">{row[2]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== KPI dashboard tiles (visible to everyone) ===== */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI onClick={() => setFilterMode('all')} active={filterMode === 'all'} icon={Wrench} label="Total Records" value={totalCount} accent="slate" />
        <KPI onClick={() => setFilterMode('overdue')} active={filterMode === 'overdue'} icon={AlertTriangle} label="Overdue" value={overdueRecords.length} accent="red" />
        <KPI onClick={() => setFilterMode('due_soon')} active={filterMode === 'due_soon'} icon={CalendarClock} label="Due Soon (30d)" value={dueSoonRecords.length} accent="amber" />
        <KPI onClick={() => setFilterMode('scheduled')} active={filterMode === 'scheduled'} icon={Activity} label="Scheduled / In Progress" value={scheduledCount} accent="sky" />
        <KPI onClick={() => setFilterMode('completed')} active={filterMode === 'completed'} icon={CheckCircle2} label="Completed Cost" value={formatCurrency(totalCost)} accent="emerald" isCurrency />
      </div>

      <PageHeader title="Maintenance Records" description="Manage preventive and corrective maintenance for your fleet."
        actions={canEdit && <Button onClick={openNew} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"><Plus className="h-4 w-4 mr-1.5" />Log Maintenance</Button>} />

      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left text-slate-600">
            <tr><th className="p-3">Equipment</th><th className="p-3">Type</th><th className="p-3">Description</th><th className="p-3">Performed</th><th className="p-3">Next Due</th><th className="p-3">Cost</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y">
            {filtered.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-slate-500">{filterMode === 'all' ? 'No maintenance records yet.' : 'No records match this filter.'}</td></tr> :
              filtered.map((m) => { const v = vehById(m.vehicle_id); const overdue = isOverdue(m); const dueSoon = isDueSoon(m); return (
                <tr key={m.id} className={`table-row-hover ${overdue ? 'bg-red-50/40' : dueSoon ? 'bg-amber-50/40' : ''}`}>
                  <td className="p-3"><EquipmentCell vehicle={v} /></td>
                  <td className="p-3"><Badge variant="secondary" className={m.maintenance_type === 'preventive' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>{m.maintenance_type}</Badge></td>
                  <td className="p-3 max-w-md">{m.description}</td>
                  <td className="p-3 text-xs">{formatDate(m.date_performed)}</td>
                  <td className="p-3 text-xs">
                    {m.next_due ? (
                      <div className="flex items-center gap-1.5">
                        {formatDate(m.next_due)}
                        {overdue && <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px]">Overdue</Badge>}
                        {dueSoon && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">Due soon</Badge>}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="p-3 text-xs">{formatCurrency(m.cost)}</td>
                  <td className="p-3"><Badge className={{scheduled:'bg-amber-100 text-amber-800 hover:bg-amber-100',in_progress:'bg-sky-100 text-sky-800 hover:bg-sky-100',completed:'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'}[m.status]}>{m.status}</Badge></td>
                  <td className="p-3 text-right">{canEdit && <Button size="sm" variant="ghost" onClick={() => openEdit(m)}><Edit2 className="h-4 w-4" /></Button>}{canDelete && <Button size="sm" variant="ghost" onClick={() => remove(m)}><Trash2 className="h-4 w-4 text-red-600" /></Button>}</td>
                </tr>); })}
          </tbody>
        </table>
      </CardContent></Card>

      {/* ===== SECTION 3: CTA / Next Steps ===== */}
      <Card className="border-slate-200">
        <CardContent className="p-6 lg:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="max-w-2xl">
            <h3 className="text-lg font-semibold text-slate-900">Ready to make your equipment fleet predictable?</h3>
            <p className="text-sm text-slate-600 mt-1">
              Set up your preventive schedules in under 15 minutes, log your first PM record, and let ESD take care of the reminders. You can also <span className="font-medium text-[hsl(217,91%,24%)]">integrate with your current software</span> to push completed records back to finance, or run ESD standalone across <span className="font-semibold">{fleetSize}</span> equipment units.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            {canEdit && <Button onClick={openNew} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"><Plus className="h-4 w-4 mr-1.5" /> Schedule Preventive Maintenance</Button>}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? 'Edit Maintenance' : 'Log Maintenance'}</DialogTitle><DialogDescription>Setting status to in-progress will mark the equipment as 'maintenance'.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Equipment *</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select equipment" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}><EquipmentOptionLabel vehicle={v} /></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Type</Label>
              <Select value={form.maintenance_type} onValueChange={(v) => setForm({ ...form, maintenance_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="preventive">Preventive</SelectItem><SelectItem value="corrective">Corrective</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="scheduled">Scheduled</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description *</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Performed By</Label><Input value={form.performed_by} onChange={(e) => setForm({ ...form, performed_by: e.target.value })} /></div>
            <div><Label>Cost (PHP)</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
            <div><Label>Date Performed</Label><Input type="date" value={form.date_performed || ''} onChange={(e) => setForm({ ...form, date_performed: e.target.value })} /></div>
            <div><Label>Next Due</Label><Input type="date" value={form.next_due || ''} onChange={(e) => setForm({ ...form, next_due: e.target.value })} /></div>
            <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks || ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">{editing ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Maintenance;

const KPI = ({ icon: Icon, label, value, accent = 'slate', active, onClick, isCurrency }) => {
  const palette = {
    slate: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
    red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    sky: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  }[accent] || { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' };
  return (
    <button onClick={onClick} className={`text-left rounded-lg border bg-white p-3 transition ${active ? 'border-[hsl(217,91%,24%)] ring-2 ring-[hsl(217,91%,24%)]/15' : 'border-slate-200 hover:border-slate-300'}`}>
      <div className="flex items-center gap-2">
        <div className={`h-8 w-8 rounded-md grid place-items-center ${palette.bg} ${palette.text} ${palette.border} border`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] text-slate-500 truncate">{label}</div>
          <div className={`text-xl font-bold leading-tight ${isCurrency ? 'text-base' : ''} text-slate-900`}>{value}</div>
        </div>
      </div>
    </button>
  );
};
